import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import { MolyLogoFull } from '@/components/app/MolyLogoFull'
import { cn } from '@/lib/utils'

interface AuthSplitLayoutProps {
  /** Left column content, vertically centred in a ~380px column. */
  children: ReactNode
  /** Right panel content. Hidden below `lg`. Omit for a single centred column. */
  preview?: ReactNode
  /** Replaces the default "Terms · Privacy" footer at the bottom-left. */
  footer?: ReactNode
  /** Widen the form column (onboarding needs room for side-by-side cards). */
  wide?: boolean
}

/**
 * Split shell shared by sign-in and onboarding (design turn 12b / 14): logo
 * and form on the left, a quiet brand-tint product preview on the right, so
 * signing in and setting up feel like one flow.
 */
export function AuthSplitLayout({ children, preview, footer, wide = false }: AuthSplitLayoutProps) {
  return (
    <div
      className={cn(
        'grid min-h-svh w-full',
        preview ? 'lg:grid-cols-2 lg:gap-4 lg:p-4' : '',
      )}
    >
      <div className="flex min-w-0 flex-col px-4 py-6 sm:px-10 lg:px-12 lg:py-8">
        <div className="shrink-0">
          <MolyLogoFull className="h-9" />
        </div>
        <main className="flex flex-1 items-center justify-center py-10">
          <div className={cn('w-full', wide ? 'max-w-[440px]' : 'max-w-[380px]')}>
            {children}
          </div>
        </main>
        <div className="shrink-0 text-xs text-muted-foreground">
          {footer ?? <AuthLegalLinks />}
        </div>
      </div>
      {preview ? (
        <aside
          aria-hidden="true"
          className="relative hidden min-w-0 overflow-hidden rounded-2xl bg-brand-tint lg:block dark:bg-brand-tint/40"
        >
          {preview}
        </aside>
      ) : null}
    </div>
  )
}

export function AuthLegalLinks() {
  const { t } = useTranslation('auth')
  return (
    <nav className="flex items-center gap-2">
      <a
        href="https://moly.so/terms"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-foreground"
      >
        {t('layout.terms')}
      </a>
      <span aria-hidden="true">·</span>
      <a
        href="https://moly.so/privacy"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-foreground"
      >
        {t('layout.privacy')}
      </a>
    </nav>
  )
}
